import { useEffect, useState } from 'react';

import { datadogLogs } from '@datadog/browser-logs';
import { FormikErrors, useFormik } from 'formik';
import { Helmet } from 'react-helmet';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router';
import classNames from 'classnames';
import InfoIcon from '@/assets/icons/info-mono.svg?react';
import { useAuth } from '@/hooks/webapi';
import Button from '@/components/Button/Button';
import { Card, MultiField, Notification } from '@/components';
import Link from '@/components/Link/Link';
import styles from './TwoFA.module.scss';
import H1 from '@/components/H/H1';
import useHandleLogin from '@/pages/Login/useHandleLogin';
import { useKeycloakStore } from '@/hooks/zustandStore';

type LoginFormFields = { verificationCode: string[] };

type LocationsState = {
  encodedCredentials: string;
};

const codeRegex = RegExp(/^[0-9]{4}$/);

const validate = (values: LoginFormFields) => {
  const errors: FormikErrors<LoginFormFields> = {};

  const joinedCode = values.verificationCode.join('');
  if (!joinedCode || !joinedCode.match(codeRegex)) {
    errors.verificationCode = 'Código incorrecto';
  }

  const hasAnyError = Object.values(errors).filter(Boolean).length > 0;

  return hasAnyError ? errors : {};
};

export default function TwoFA() {
  const [timeLeft, setTimeLeft] = useState(60);
  const [resendIsDisabled, setResendIsDisabled] = useState(true);
  const [pending, setPending] = useState(false);
  const { postAccessVerification, resend } = useAuth();
  const location = useLocation();
  const [serverError, setServerError] = useState('');
  const { handleLogin } = useHandleLogin();

  const isLegacyLoginLoading = useKeycloakStore(
    (state) => state.isLegacyLoginLoading,
  );

  const { t } = useTranslation(['twoFa', 'error']);

  useEffect(() => {
    if (!resendIsDisabled) {
      return;
    }

    const intervalId = setInterval(() => {
      setTimeLeft((previousTimeLeftValue) => previousTimeLeftValue - 1);
    }, 1000);

    if (timeLeft === 0) {
      setResendIsDisabled(false);
    }

    return () => {
      clearInterval(intervalId);
    };
  }, [resendIsDisabled, timeLeft]);

  const handleResendClick = async () => {
    setTimeLeft(60);
    setResendIsDisabled(true);
    resend((location.state as LocationsState).encodedCredentials);
  };

  const handleSubmit = async (values: LoginFormFields) => {
    try {
      if (!resendIsDisabled) {
        return;
      }

      setPending(true);
      await postAccessVerification(
        values.verificationCode.join(''),
        (location.state as LocationsState).encodedCredentials,
      );

      handleLogin('TwoFA');
    } catch (error) {
      datadogLogs.logger.error(`2FA FAILED`, {
        error,
      });

      if (error instanceof Error) {
        setServerError(error.message);
      } else {
        setServerError(t('error:generic'));
      }

      setPending(false);
    }
  };

  const formik = useFormik<LoginFormFields>({
    initialValues: {
      verificationCode: [],
    },
    onSubmit: handleSubmit,
    validate,
  });

  const handleChange = async (
    fieldName: string,
    values: Array<string | undefined>,
  ) => {
    await formik.setFieldValue(fieldName, values);
  };

  return (
    <div className="flex flex-col items-center">
      <Helmet>
        <title>{t('html_title')}</title>
      </Helmet>

      {serverError && (
        <div className={styles.notification}>
          <Notification
            dataTest="server-error"
            type="error"
            onClose={() => setServerError('')}
          >
            {serverError}
          </Notification>
        </div>
      )}

      <H1 dataTest="twoFA-title">{t('title')}</H1>
      <div className={styles.subheading} data-test="twoFA-subtitle">
        <p>{t('subtitle')}</p>
      </div>
      <form onSubmit={formik.handleSubmit} className="flex flex-col">
        <div className={styles['input-container']}>
          <MultiField
            dataTest="2fa"
            name="verificationCode"
            value={formik.values.verificationCode}
            onChange={handleChange}
            error={formik.errors.verificationCode as string}
            isTouched={formik.touched.verificationCode as boolean}
          />
        </div>
        <Link
          onClick={handleResendClick}
          className={classNames(
            styles.link,
            resendIsDisabled && styles.disabled,
          )}
          disabled={resendIsDisabled}
          dataTest="twoFA-link-resend"
        >
          {t('resend')} {resendIsDisabled && `(${timeLeft}s)`}
        </Link>
        <Button
          className={styles.submit}
          pending={pending || isLegacyLoginLoading}
          type="submit"
          disabled={!resendIsDisabled}
          dataTest="submit-button"
        >
          {t('submit')}
        </Button>
      </form>
      <div className={styles['have-questions']}>
        <div
          dangerouslySetInnerHTML={{
            __html: t('contact_details'),
          }}
          className={styles['have-questions-text']}
          data-test="twoFA-contact-details"
        />
      </div>
      <Card className={styles['info-card']} dataTest="twoFA-card">
        <div className={styles['info-card-icon']}>
          <InfoIcon data-test="twoFA-info-icon" />
        </div>
        <div className={styles['info-card-content']} data-test="twoFA-info">
          {t('info')}
        </div>
      </Card>
    </div>
  );
}
